<template>
  <Card class="py-6 w-full px-3 mt-5 h-full">
    <div v-if="!showError">
      <Card >
        <Tabs
          class="mt-2 mx-2 w-full"
          :tabs="tabsItems"
          :active-tab="activeTab"
          @currentTab="activeTab = $event"
          :border="true"
        />
      </Card>
      <div>
        <Pension
          v-if="activeTab === 'Pension'"
          :org-pay-setting-id="orgPaySettingId"
        />
        <div v-if="activeTab === 'PAYE'" class="w-full mt-8">
          <div class="flex">
            <div class="flex items-center gap-3 mb-6" v-if="!isPayeDisabled">
              <label class="switch">
                <input
                  type="checkbox"
                    :checked="deductEmployeeTax"
                  @change="deductEmployeeTax = $event.target.checked"
                />
                <span class="slider round"></span>
              </label>
              <p class="text-sm font-semibold text-gray-700">Enable PAYE to be deducted from employees payrun/salary</p>
            </div>
            <p v-else class="font-extrabold">{{deductEmployeeTax ? 'PAYE enabled' : 'PAYE disabled'}}</p>
            <Alert
              message="This can only be set once within a financial cycle"
              variant="primary"
              :time="2"
              style="
                position: relative;
                right: 0;
                top: -5px;
                box-shadow: none;
                background: #ffffff;
                color: #e99323;
                margin-left: 45px;
              "
            />
          </div>
          <small
            v-if="isPayeDisabled"
            class="text-flame block text-sm font-bold"
            >You do not have permission to enable or disable PAYE</small
          >
          <div v-if="!isPayeDisabled" class="pl-3 mt-9 mb-5 flex flex-row">
            <Button :class="`bg-dynamicBackBtn text-white`" @click="updatePaye">
              Save Changes
            </Button>
            <Button class="text-darkPurple ml-4" @click="reversePaye">
              Revert Changes
            </Button>
          </div>
        </div>
        <div v-if="activeTab === 'NHF'" class="w-full mt-8">
          <div>
            <div class="flex items-center gap-3 mb-6" v-if="!isNhfDisabled">
              <label class="switch">
                <input
                  type="checkbox"
                    :checked="deductEmployeeNHF"
                  @change="deductEmployeeNHF = $event.target.checked"
                />
                <span class="slider round"></span>
              </label>
              <p class="text-sm font-semibold text-gray-700">Deduct NHF contribution from employee salary.</p>
            </div>
            <p v-else class="font-extrabold">{{deductEmployeeNHF ? 'NHF enabled' : 'NHF disabled'}}</p>
            <small
            v-if="isNhfDisabled"
            class="text-flame block text-sm font-bold"
            >You do not have permission to enable or disable NHF</small
          >
            <p class="ml-1 mt-8 font-medium">
              <span class="text-xl font-bold">{{ rateValue }}% </span>of
              employee's basic salary
            </p>
          </div>
          <div v-if="!isNhfDisabled" class="pl-3 mt-9 mb-5 flex flex-row">
            <Button :class="`bg-dynamicBackBtn text-white`" @click="updateNhf">
              Save Changes
            </Button>
            <Button class="text-darkPurple ml-4" @click="reverseNhf">
              Revert Changes
            </Button>
          </div>
        </div>
        <Nhis v-if="activeTab === 'NHIS'" :is-active="activeTab === 'NHIS'" />
      </div>
    </div>
    <div v-else>
      <error-page />
    </div>
  </Card>
</template>

<script>
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import Alert from "@scelloo/cloudenly-ui/src/components/alert";
import Card from "@/components/Card";
import ErrorPage from "@/modules/Admin/error403";
import Button from "@/components/Button";
import Pension from './Pension';
import Nhis from './Nhis';

export default {
  components: {
    Tabs,
    Card,
    Button,
    ErrorPage,
    Alert,
    Pension,
    Nhis,
  },
  props: {
    orgPaySettingId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      activeTab: "Pension",
      showDescription: false,
      enabledView: false,
      kpaModal: false,
      isNhfDisabled: true,
      isPayeDisabled: true,
      tabsItems: ["Pension", "PAYE", "NHF", "NHIS"],
      deductEmployeeTax: false,
      deductEmployeeNHF: false,
      showError: false,
      rateValue: 0,
    };
  },
  methods: {
    reversePaye() {
      this.deductEmployeeTax = false;
    },
    reverseNhf() {
      this.deductEmployeeNHF = false;
    },
    async updatePaye() {
      try {
        const payload = {
          orgId: this.$orgId,
          isEnabled: this.deductEmployeeTax,
        };
        if (this.orgPaySettingId) {
          payload.orgPaySettingId = this.orgPaySettingId;
        }
        await this.$_savePayeSettings(payload);
        this.$toasted.success("Settings saved", {
          duration: 5000,
        });
      } catch (error) {
        this.$toasted.error("Settings not saved", {
          duration: 5000,
        });
      }
    },
    async updateNhf() {
      try {
        const payload = {
          orgId: this.$orgId,
          isEnabled: this.deductEmployeeNHF,
        };
        if (this.orgPaySettingId) {
          payload.orgPaySettingId = this.orgPaySettingId;
        }
        await this.$_saveNhfSettings(payload);
        this.$toasted.success("Settings saved", {
          duration: 5000,
        });
      } catch (error) {
        this.$toasted.error("Settings not saved", {
          duration: 5000,
        });
      }
    },
    async getPaye() {
      this.enableOrDisablePaye()
      const orgPaySettingId = this.orgPaySettingId || this.$store.state.paySettingsActiveIds.ACTIVE || '';
      const { data } = await this.$_getPayeSettings(`?orgPaySettingId=${orgPaySettingId}`);
      this.deductEmployeeTax = data.setting.isEnabled;
    },
    async getNhf() {
      this.enableOrDisableNhf()
      const orgPaySettingId = this.orgPaySettingId || this.$store.state.paySettingsActiveIds.ACTIVE || '';
      const { data } = await this.$_getNhfSettings(`?orgPaySettingId=${orgPaySettingId}`);
      this.deductEmployeeNHF = data.setting.isEnabled;
      this.rateValue = data.setting.rateValue;
    },
    async enableOrDisablePaye() {
      try {
        await this.$handlePrivilege("paySettings", "enableOrDisablePaye");
        this.isPayeDisabled = false;
      } catch (error) {
        this.isPayeDisabled = true;
      }
    },
    async enableOrDisableNhf() {
      try {
        await this.$handlePrivilege("paySettings", "enableOrDisableNhf");
        this.isNhfDisabled = false;
      } catch (error) {
        this.isNhfDisabled = true;
      }
    },
  },

  async mounted() {
    try {
      await this.$handlePrivilege("paySettings", "accessComplianceSettings");
      this.getPaye();
      this.getNhf();
      this.showError = false;
    } catch (error) {
      this.showError = true;
    }
  },
};
</script>

<style scoped>
.border__dashed {
  height: 0px;
  margin: 18px 0px;
  border: 1px dashed #878e99;
}

.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EEEEEE;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  border-radius: 50%;
  left: 2px;
  bottom: 2px;
  background-color: #ffffff;
  transition: 0.4s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}


input:checked + .slider {
  background-color: #EEEEEE;
}

input:checked + .slider:before {
  transform: translateX(26px);
  background-color: #ffffff;
}

.switch {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 12px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EEEEEE;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 9.6px;
  width: 9.6px;
  border-radius: 50%;
  left: 2px;
  bottom: 1.5px;
  background-color: #878E99;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #EEEEEE;
}
input:checked+.slider:before {
  transform: translateX(10px);
  background-color:#13B56A;
}

.switch-header {
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 600;
  color: #878E99;
}
</style>
